/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Wallet, Phone, Send, Smartphone, LogOut, ShieldCheck, ArrowRight, Plus, Snowflake, Gift, MessageSquare, Users } from "lucide-react";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import { walletApi, gbp } from "@/lib/wallet";
import NotificationsBell from "@/components/customer/NotificationsBell";
import SEO from "@/components/site/SEO";
import CustomerBottomNav from "@/components/customer/CustomerBottomNav";
import BrandLogo from "@/components/site/BrandLogo";

export default function CustomerDashboard() {
  const { user, logout } = useCustomerAuth();
  const nav = useNavigate();
  const [wallet, setWallet] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await walletApi.get("/customer/wallet/me");
        setWallet(data);
      } catch (err) {
        // Wallet endpoint can 401 briefly before auth context hydrates — log but don't toast.
        console.warn("Wallet snapshot deferred", err?.response?.status || err?.message);
      }
    })();
  }, []);

  const onLogout = async () => { await logout(); nav("/login", { replace: true }); };
  const isFrozen = wallet?.status === "frozen";

  return (
    <div className="min-h-screen bg-brand-bgSoft text-brand-ink pb-24 md:pb-0" data-testid="customer-dashboard">
      <SEO title="Dashboard" path="/app" />
      <header className="bg-white border-b border-brand-line sticky top-0 z-30">
        <div className="container-page flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3">
            <BrandLogo variant="wordmark" className="h-8 w-auto" />
            <div className="leading-tight">
              <div className="font-display font-extrabold">
                OneDial <span className="text-brand-green">Africa</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted">My account</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <NotificationsBell />
            <Link to="/app/profile" data-testid="dash-link-profile" className="hidden sm:inline text-sm font-semibold text-brand-ink/80 hover:text-brand-ink">
              Profile
            </Link>
            <button type="button" onClick={onLogout} data-testid="dash-logout"
              className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white px-4 py-2 text-xs font-semibold text-brand-ink hover:bg-brand-bgSoft">
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="container-page py-10 grid lg:grid-cols-12 gap-6">
        <section className="lg:col-span-8 space-y-6">
          <div className="rounded-2xl bg-brand-ink text-white p-8 lg:p-10 relative overflow-hidden">
            <div className="absolute inset-0 bg-grid-overlay opacity-30" />
            <div className="relative">
              <p className="label-eyebrow text-brand-green">Welcome back</p>
              <h1 className="mt-3 font-display font-black text-3xl sm:text-4xl">Hi {user?.name?.split(" ")[0] || "there"} 👋</h1>
              <p className="mt-3 text-white/70 max-w-xl">
                Your wallet, calls and transfers in one place.
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-brand-line bg-white p-6 lg:p-8" data-testid="dash-wallet-card">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="label-eyebrow">Wallet</p>
                <h2 className="mt-2 font-display font-black text-2xl" data-testid="dash-wallet-balance">
                  {wallet ? gbp(wallet.balance) : "…"} <span className="text-base text-brand-muted font-semibold">GBP</span>
                </h2>
                <p className="mt-1 text-xs text-brand-muted">Closed-loop service credits · GBP only</p>
              </div>
              <div className="flex items-center gap-2">
                {isFrozen && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 px-3 py-1.5 text-xs font-semibold" data-testid="dash-frozen-badge">
                    <Snowflake className="h-3 w-3" /> Frozen
                  </span>
                )}
                <Link to="/app/wallet" data-testid="dash-open-wallet"
                  className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white px-4 py-2 text-xs font-semibold text-brand-ink hover:bg-brand-bgSoft">
                  Open wallet <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <Link to="/app/wallet/topup" data-testid="dash-topup-cta"
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-white shadow-sm ${isFrozen ? "bg-brand-muted cursor-not-allowed pointer-events-none" : "bg-brand-green hover:bg-brand-greenHover"}`}>
                  <Plus className="h-3.5 w-3.5" /> Top up
                </Link>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                {i:Phone,l:"Call",to:"/app/dial"},
                {i:Users,l:"Contacts",to:"/app/contacts"},
                {i:Wallet,l:"Top up", to:"/app/wallet/topup"},
                {i:Gift,l:"Earn & Gift", to:"/app/earn"},
                {i:Send,l:"Send",to:null},
                {i:Smartphone,l:"Airtime",to:null},
                {i:MessageSquare,l:"Support", to:"/app/support"},
              ].map(({i:Icon,l, to}) => (
                <Link key={l} to={to || "#"} data-testid={`dash-tile-${l.toLowerCase().replace(/[^a-z]/g,"-")}`}
                  className={`rounded-xl border border-brand-line bg-brand-bgSoft p-4 flex flex-col items-center justify-center gap-2 ${to ? "hover:border-brand-green hover:bg-white" : "opacity-60 pointer-events-none"}`}>
                  <Icon className="h-5 w-5 text-brand-greenDark" />
                  <span className="text-xs font-semibold">{l}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <aside className="lg:col-span-4 space-y-6">
          <div className="rounded-2xl border border-brand-line bg-white p-6">
            <p className="label-eyebrow">Account</p>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex items-center justify-between"><span className="text-brand-muted">Name</span><span className="font-semibold">{user?.name}</span></div>
              <div className="flex items-center justify-between"><span className="text-brand-muted">Email</span><span className="font-semibold">{user?.email}</span></div>
              <div className="flex items-center justify-between"><span className="text-brand-muted">Country</span><span className="font-semibold">{user?.country}</span></div>
              <div className="flex items-center justify-between"><span className="text-brand-muted">Email verified</span>
                <span className={`font-semibold ${user?.email_verified ? "text-brand-greenDark" : "text-red-600"}`}>
                  {user?.email_verified ? "Yes" : "No"}
                </span>
              </div>
            </div>
            <Link to="/app/profile" data-testid="dash-profile-cta"
              className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-brand-green hover:text-brand-greenHover">
              Manage profile <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          <div className="rounded-2xl border border-brand-line bg-white p-6">
            <p className="label-eyebrow">Security</p>
            <div className="mt-4 flex items-center gap-3 text-sm text-brand-ink">
              <ShieldCheck className="h-5 w-5 text-brand-green" />
              <div>
                <p className="font-semibold">Account protected</p>
                <p className="text-brand-muted">Encrypted by default. OTP available for sensitive actions.</p>
              </div>
            </div>
          </div>
        </aside>
      </div>
      <CustomerBottomNav />
    </div>
  );
}
